import React, { useEffect, useRef, useState, useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import { FaTrash, FaExternalLinkAlt, FaExclamationTriangle } from "react-icons/fa";
import { CUENTAS_ER, TIPOS_ACTIVO, DESTINO_TIPOS, getVidaUtilPorTipo } from "../../constants/clasificacionContable";
import { extraerNeto } from "../../utils/calcularImpuesto";
import styles from "./FlujoCaja.module.scss";

// =====================================================
// CONSTANTES
// =====================================================

const MONEDAS_POR_PAIS = {
  Chile: ["CLP", "USD", "UF"],
  Perú: ["PEN", "USD"],
};

const PREFIJO_MONEDA = { CLP: "$", PEN: "S/", USD: "US$", UF: "UF" };

const NOMBRE_IMPUESTO = { Chile: "IVA", Perú: "IGV" };

const ESTADO_LABELS = {
  pendiente: "Pendiente",
  parcial: "Parcial",
  pagada: "Pagada",
  anulada: "Anulada",
};

const CUENTA_ER_LABELS = {};
CUENTAS_ER.forEach((c) => {
  CUENTA_ER_LABELS[c.value] = c.label;
});

const TIPO_ACTIVO_LABELS = {};
TIPOS_ACTIVO.forEach((t) => {
  TIPO_ACTIVO_LABELS[t.value] = t.label;
});

// =====================================================
// HELPERS
// =====================================================

const formatFechaDisplay = (fechaStr) => {
  if (!fechaStr) return "-";
  const [year, month, day] = fechaStr.split("-");
  return `${day}/${month}/${year}`;
};

const parseFechaLocal = (fechaStr) => {
  if (!fechaStr) return null;
  const [year, month, day] = fechaStr.split("-");
  return new Date(year, month - 1, day);
};

const formatFechaToISO = (date) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatMontoDisplay = (value, pais) => {
  if (!value && value !== 0) return "";
  // String (tipeo en curso): devolver tal cual para no interrumpir el ingreso
  if (typeof value === "string") return value;
  // Número: formatear con separador de miles y hasta 2 decimales
  const locale = pais === "Chile" ? "es-CL" : "es-PE";
  return value.toLocaleString(locale, { maximumFractionDigits: 2 });
};

const parseMontoInput = (displayValue, moneda) => {
  // Permitir dígitos, punto y coma para todas las monedas
  const clean = displayValue.replace(/[^\d,.]/g, "");
  if (!clean) return "";
  const lastDot = clean.lastIndexOf(".");
  const lastComma = clean.lastIndexOf(",");
  let numStr;
  if (lastDot >= 0 && lastComma >= 0) {
    numStr = lastDot > lastComma ? clean.replace(/,/g, "") : clean.replace(/\./g, "").replace(",", ".");
  } else if (lastComma >= 0) {
    numStr = clean.replace(",", ".");
  } else if (lastDot >= 0) {
    const afterDot = clean.slice(lastDot + 1);
    const isMiles = (moneda === "CLP" || moneda === "PEN") && afterDot.length === 3 && !clean.endsWith(".");
    numStr = isMiles ? clean.replace(/\./g, "") : clean;
  } else {
    numStr = clean;
  }
  if (numStr.endsWith(".")) return numStr;
  const num = parseFloat(numStr);
  return isNaN(num) ? "" : num;
};

// =====================================================
// FORM INICIAL
// =====================================================

const getInitialForm = (normalizedPais) => ({
  proveedor_id: "",
  numero_factura: "",
  fecha_emision: formatFechaToISO(new Date()),
  fecha_vencimiento: "",
  monto_total: "",
  moneda: normalizedPais === "Chile" ? "CLP" : "PEN",
  descripcion: "",
  exento_impuesto: false,
  // Clasificación contable
  tipo_contable: "",
  cuenta_er: "",
  // Campos activo
  activo_tipo: "",
  activo_descripcion: "",
  activo_numero_serie: "",
  activo_cantidad: 1,
  activo_destino_tipo: "",
  activo_destino_id: "",
});

// =====================================================
// COMPONENTE
// =====================================================

const FacturasProveedorTab = ({
  facturas,
  proveedores,
  normalizedPais,
  formatAmount,
  onCrearFactura,
  onCrearProveedor,
  onSubirArchivo,
  onAnularFactura,
  edificios = [],
  proyectosCarteleria = [],
  canEdit = true,
  autoOpenModal = false,
  onAutoOpenModalHandled,
  siiRecibidasVinculadas = [],
}) => {
  const enteFiscal = normalizedPais === "Chile" ? "SII" : "SUNAT";
  // Set de facturas verificadas en SII (folio+proveedor_id)
  const siiSet = useMemo(() => {
    const set = new Set();
    siiRecibidasVinculadas.forEach((s) => set.add(`${s.folio}_${s.proveedor_id}`));
    return set;
  }, [siiRecibidasVinculadas]);
  const tieneVerificacionSII = (f) => siiSet.has(`${f.numero_factura}_${f.proveedor_id}`);

  const [showModal, setShowModal] = useState(false);
  const [filtroProveedor, setFiltroProveedor] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [confirmAnular, setConfirmAnular] = useState(null);
  const [uploading, setUploading] = useState(null);
  const fileInputRef = useRef(null);

  // Auto-abrir modal desde el padre (botón "+ Factura" del header)
  useEffect(() => {
    if (autoOpenModal) {
      setShowModal(true);
      if (onAutoOpenModalHandled) onAutoOpenModalHandled();
    }
  }, [autoOpenModal, onAutoOpenModalHandled]);

  // Crear proveedor inline
  const [showNuevoProveedor, setShowNuevoProveedor] = useState(false);
  const [nuevoProveedorNombre, setNuevoProveedorNombre] = useState("");
  const [creandoProveedor, setCreandoProveedor] = useState(false);

  // Form nueva factura
  const [form, setForm] = useState(getInitialForm(normalizedPais));
  const [saving, setSaving] = useState(false);
  const [archivo, setArchivo] = useState(null);

  const handleCrearProveedorInline = async () => {
    const nombre = nuevoProveedorNombre.trim();
    if (!nombre || !onCrearProveedor) return;
    setCreandoProveedor(true);
    try {
      const nuevo = await onCrearProveedor(nombre);
      if (nuevo) {
        setForm((p) => ({ ...p, proveedor_id: String(nuevo.id) }));
        setShowNuevoProveedor(false);
        setNuevoProveedorNombre("");
      }
    } finally {
      setCreandoProveedor(false);
    }
  };

  const monedas = MONEDAS_POR_PAIS[normalizedPais] || ["USD"];

  const tasaImpuesto = normalizedPais === "Chile" ? 0.19 : 0.18;
  const nombreImpuesto = normalizedPais === "Chile" ? "IVA" : "IGV";

  const facturasFiltradas = useMemo(() => {
    let filtered = [...facturas];
    if (filtroProveedor) filtered = filtered.filter((f) => f.proveedor_id === Number(filtroProveedor));
    if (filtroEstado) filtered = filtered.filter((f) => f.estado === filtroEstado);
    filtered.sort((a, b) => (b.fecha_emision || "").localeCompare(a.fecha_emision || ""));
    return filtered;
  }, [facturas, filtroProveedor, filtroEstado]);

  // Mapa proveedor_id -> nombre
  const proveedorNombre = useMemo(() => {
    const map = {};
    proveedores.forEach((p) => {
      map[p.id] = p.nombre;
    });
    return map;
  }, [proveedores]);

  // Resumen de deuda del proveedor seleccionado
  const deudaProveedor = useMemo(() => {
    if (!filtroProveedor) return null;
    const provId = Number(filtroProveedor);
    const factsDelProv = facturas.filter((f) => f.proveedor_id === provId && f.estado !== "pagada");
    const totalDeuda = factsDelProv.reduce((sum, f) => sum + (Number(f.monto_total) - Number(f.monto_pagado)), 0);
    const cantFacturas = factsDelProv.length;
    const moneda = factsDelProv[0]?.moneda || (normalizedPais === "Chile" ? "CLP" : "PEN");
    return { totalDeuda, cantFacturas, moneda, nombre: proveedorNombre[Number(filtroProveedor)] || "" };
  }, [filtroProveedor, facturas, proveedorNombre, normalizedPais]);

  // Vida útil calculada
  const vidaUtilActivo = form.activo_tipo ? getVidaUtilPorTipo(form.activo_tipo) : null;

  const resetForm = () => setForm(getInitialForm(normalizedPais));

  const handleSubmit = async () => {
    const {
      proveedor_id,
      numero_factura,
      fecha_emision,
      fecha_vencimiento,
      monto_total,
      moneda: mon,
      descripcion,
      tipo_contable,
      cuenta_er,
      activo_tipo,
      activo_descripcion,
      activo_numero_serie,
      activo_cantidad,
      activo_destino_tipo,
      activo_destino_id,
    } = form;
    if (!proveedor_id || !numero_factura.trim() || !fecha_emision || !monto_total) return;
    if (!tipo_contable) return;
    if (tipo_contable === "gasto" && !cuenta_er) return;
    if (tipo_contable === "activo" && (!activo_tipo || !activo_descripcion.trim())) return;

    // Validar duplicado: mismo proveedor + mismo número de factura
    const yaExiste = facturas.some(
      (f) => f.proveedor_id === Number(proveedor_id) && f.numero_factura === numero_factura.trim()
    );
    if (yaExiste) {
      toast.error(`Ya existe la factura ${numero_factura.trim()} para este proveedor`);
      return;
    }

    // monto_total en form = neto ingresado; calcular total con IVA para guardar
    const netoNum = Number(monto_total);
    const totalConIVA = formExentoImpuesto ? netoNum : Math.round(netoNum * (1 + tasaImpuesto));

    setSaving(true);
    try {
      await onCrearFactura(
        {
          proveedor_id: Number(proveedor_id),
          numero_factura: numero_factura.trim(),
          fecha_emision,
          fecha_vencimiento: fecha_vencimiento || null,
          monto_total: totalConIVA,
          moneda: mon,
          descripcion: descripcion.trim() || null,
          exento_impuesto: formExentoImpuesto || false,
          tipo_contable,
          cuenta_er: tipo_contable === "gasto" ? cuenta_er : null,
          activo_tipo,
          activo_descripcion,
          activo_numero_serie,
          activo_cantidad,
          activo_destino_tipo,
          activo_destino_id,
        },
        archivo
      );
      setShowModal(false);
      resetForm();
      setArchivo(null);
    } finally {
      setSaving(false);
    }
  };

  const handleUploadClick = (facturaId) => {
    setUploading(facturaId);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploading) return;
    await onSubirArchivo(uploading, file);
    setUploading(null);
    e.target.value = "";
  };

  const handleAnular = async (facturaId) => {
    await onAnularFactura(facturaId);
    setConfirmAnular(null);
  };

  // Destructurar form para evitar unicorn/consistent-destructuring
  const {
    proveedor_id: formProveedorId,
    numero_factura: formNumFactura,
    fecha_emision: formFechaEmision,
    fecha_vencimiento: formFechaVenc,
    monto_total: formMontoTotal,
    moneda: formMoneda,
    descripcion: formDescripcion,
    tipo_contable: formTipoContable,
    cuenta_er: formCuentaEr,
    activo_tipo: formActivoTipo,
    activo_descripcion: formActivoDesc,
    activo_numero_serie: formActivoSerie,
    activo_cantidad: formActivoCantidad,
    activo_destino_tipo: destTipo,
    activo_destino_id: formActivoDestinoId,
    exento_impuesto: formExentoImpuesto,
  } = form;

  // Opciones destino condicional
  let destinoOpciones = [];
  if (destTipo === "edificio") {
    destinoOpciones = edificios.map((e) => ({ value: e.id, label: e.edificio }));
  } else if (destTipo === "carteleria") {
    destinoOpciones = proyectosCarteleria.map((p) => ({ value: p.id, label: p.nombre_proyecto || p.referencia_id }));
  }

  // Label clasificación para la tabla
  const getClasificacionLabel = (f) => {
    if (f.tipo_contable === "gasto") return CUENTA_ER_LABELS[f.cuenta_er] || f.cuenta_er || "Gasto";
    if (f.tipo_contable === "activo") return TIPO_ACTIVO_LABELS[f.activo_tipo] || "Activo";
    return "-";
  };

  // Agrupación de cuentas ER para optgroup
  const cuentasFijos = CUENTAS_ER.filter((c) => c.grupo === "Costos Fijos");
  const cuentasVariables = CUENTAS_ER.filter((c) => c.grupo === "Costos Variables");

  return (
    <>
      {/* Input oculto para subir archivo a factura existente */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp"
        style={{ display: "none" }}
        onChange={handleFileSelected}
      />

      {/* Filtros + botón */}
      <div className={styles.filterBar}>
        <select className="form-select" value={filtroProveedor} onChange={(e) => setFiltroProveedor(e.target.value)}>
          <option value="">Proveedor</option>
          {proveedores.map((p) => (
            <option key={p.id} value={p.id}>
              {p.nombre}
            </option>
          ))}
        </select>
        <select className="form-select" value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
          <option value="">Estado</option>
          <option value="pendiente">Pendiente</option>
          <option value="parcial">Parcial</option>
          <option value="pagada">Pagada</option>
        </select>
        {canEdit && (
          <button className="btn btn-dark btn-sm" onClick={() => setShowModal(true)}>
            + Nueva Factura
          </button>
        )}
      </div>

      {/* Resumen deuda proveedor */}
      {deudaProveedor && deudaProveedor.cantFacturas > 0 && (
        <div className={styles.deudaResumen}>
          <span className={styles.deudaLabel}>Deuda pendiente con {deudaProveedor.nombre}:</span>
          <span className={styles.deudaMonto}>{formatAmount(deudaProveedor.totalDeuda, deudaProveedor.moneda)}</span>
          <span className={styles.deudaDetalle}>
            ({deudaProveedor.cantFacturas} factura{deudaProveedor.cantFacturas > 1 ? "s" : ""} pendiente
            {deudaProveedor.cantFacturas > 1 ? "s" : ""})
          </span>
        </div>
      )}

      {/* Tabla */}
      {facturasFiltradas.length === 0 ? (
        <div className={styles.empty}>No hay facturas registradas</div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.facturasTable}>
            <thead>
              <tr>
                <th>N° Factura</th>
                <th>Proveedor</th>
                <th>Tipo</th>
                <th>Emisión</th>
                <th>Neto</th>
                <th>{nombreImpuesto}</th>
                <th>Total</th>
                <th>Pagado</th>
                <th>Pendiente</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {facturasFiltradas.map((f) => {
                const total = Number(f.monto_total) || 0;
                const esExento = f.exento_impuesto;
                const { neto, impuesto } = extraerNeto(total, normalizedPais, esExento);
                const pendiente = total - Number(f.monto_pagado);
                return (
                  <tr key={f.id}>
                    <td>
                      {f.numero_factura}
                      {tieneVerificacionSII(f) && (
                        <span
                          title={`Verificado ${enteFiscal}`}
                          style={{
                            marginLeft: 6,
                            fontSize: "0.6rem",
                            color: "#27ae60",
                            fontWeight: 700,
                          }}
                        >
                          {enteFiscal} ✓
                        </span>
                      )}
                    </td>
                    <td>{proveedorNombre[f.proveedor_id] || "-"}</td>
                    <td style={{ textAlign: "center" }}>
                      {f.tipo_contable === "gasto" && (
                        <span className={styles.tipoBadgeGasto}>{getClasificacionLabel(f)}</span>
                      )}
                      {f.tipo_contable === "activo" && (
                        <span className={styles.tipoBadgeActivo}>{getClasificacionLabel(f)}</span>
                      )}
                      {!f.tipo_contable && "-"}
                    </td>
                    <td style={{ textAlign: "center" }}>{formatFechaDisplay(f.fecha_emision)}</td>
                    <td style={{ textAlign: "right" }}>{formatAmount(neto, f.moneda)}</td>
                    <td style={{ textAlign: "right" }}>{impuesto ? formatAmount(impuesto, f.moneda) : "-"}</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>{formatAmount(total, f.moneda)}</td>
                    <td
                      style={{ textAlign: "right" }}
                      className={Number(f.monto_pagado) > 0 ? styles.montoPositivo : ""}
                    >
                      {formatAmount(f.monto_pagado, f.moneda)}
                    </td>
                    <td style={{ textAlign: "right" }} className={pendiente > 0 ? styles.montoPendiente : ""}>
                      {formatAmount(pendiente, f.moneda)}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <span className={`${styles.estadoBadge} ${styles[f.estado]}`}>
                        {ESTADO_LABELS[f.estado] || f.estado}
                      </span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {canEdit && confirmAnular === f.id ? (
                        <>
                          <button
                            className={`${styles.actionBtn} ${styles.deleteBtn}`}
                            onClick={() => handleAnular(f.id)}
                            style={{ color: "#c62828", fontWeight: 600, fontSize: "0.7rem" }}
                          >
                            Sí
                          </button>
                          <button
                            className={styles.actionBtn}
                            onClick={() => setConfirmAnular(null)}
                            style={{ fontSize: "0.7rem" }}
                          >
                            No
                          </button>
                        </>
                      ) : (
                        <>
                          {f.archivo_url ? (
                            <a
                              href={f.archivo_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Ver archivo"
                              className={styles.actionBtn}
                            >
                              <FaExternalLinkAlt />
                            </a>
                          ) : canEdit ? (
                            <button
                              className={`${styles.actionBtn} ${styles.sinComprobante}`}
                              onClick={() => handleUploadClick(f.id)}
                              title="Pendiente de comprobante - Subir archivo"
                            >
                              <FaExclamationTriangle />
                            </button>
                          ) : null}
                          {canEdit && (
                            <button
                              className={`${styles.actionBtn} ${styles.deleteBtn}`}
                              onClick={() => setConfirmAnular(f.id)}
                              title="Anular factura"
                            >
                              <FaTrash />
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal nueva factura */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h5>Nueva Factura de Proveedor</h5>
              <button className="btn-close" onClick={() => setShowModal(false)} />
            </div>

            <div className={styles.modalBody}>
              <div className={styles.formGrid}>
                {/* Proveedor */}
                <div className={styles.formGroup}>
                  <label>Proveedor *</label>
                  {showNuevoProveedor ? (
                    <div style={{ display: "flex", gap: 6 }}>
                      <input
                        type="text"
                        className="form-control"
                        value={nuevoProveedorNombre}
                        onChange={(e) => setNuevoProveedorNombre(e.target.value)}
                        placeholder="Nombre del proveedor"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleCrearProveedorInline();
                          if (e.key === "Escape") {
                            setShowNuevoProveedor(false);
                            setNuevoProveedorNombre("");
                          }
                        }}
                        disabled={creandoProveedor}
                      />
                      <button
                        className="btn btn-dark btn-sm"
                        onClick={handleCrearProveedorInline}
                        disabled={!nuevoProveedorNombre.trim() || creandoProveedor}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {creandoProveedor ? "..." : "Crear"}
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          setShowNuevoProveedor(false);
                          setNuevoProveedorNombre("");
                        }}
                        disabled={creandoProveedor}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: 6 }}>
                      <select
                        className="form-select"
                        value={formProveedorId}
                        onChange={(e) => {
                          const provId = e.target.value;
                          const prov = proveedores.find((p) => String(p.id) === provId);
                          setForm((p) => ({
                            ...p,
                            proveedor_id: provId,
                            tipo_contable: prov?.tipo_contable_default || p.tipo_contable,
                            cuenta_er: prov?.cuenta_er_default || p.cuenta_er,
                          }));
                        }}
                      >
                        <option value="">Seleccionar...</option>
                        {proveedores.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.nombre}
                          </option>
                        ))}
                      </select>
                      {onCrearProveedor && (
                        <button
                          className="btn btn-outline-dark btn-sm"
                          onClick={() => setShowNuevoProveedor(true)}
                          title="Crear nuevo proveedor"
                          style={{ whiteSpace: "nowrap" }}
                        >
                          + Nuevo
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* N° Factura */}
                <div className={styles.formGroup}>
                  <label>N° Factura *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formNumFactura}
                    onChange={(e) => setForm((p) => ({ ...p, numero_factura: e.target.value }))}
                    placeholder="Ej: FAC-001"
                  />
                </div>

                {/* Fecha emisión */}
                <div className={styles.formGroup}>
                  <label>Fecha Emisión *</label>
                  <DatePicker
                    selected={parseFechaLocal(formFechaEmision)}
                    onChange={(date) => setForm((p) => ({ ...p, fecha_emision: formatFechaToISO(date) }))}
                    dateFormat="dd/MM/yyyy"
                    className="form-control"
                  />
                </div>

                {/* Fecha vencimiento */}
                <div className={styles.formGroup}>
                  <label>Fecha Vencimiento</label>
                  <DatePicker
                    selected={parseFechaLocal(formFechaVenc)}
                    onChange={(date) => setForm((p) => ({ ...p, fecha_vencimiento: formatFechaToISO(date) }))}
                    dateFormat="dd/MM/yyyy"
                    className="form-control"
                    isClearable
                    placeholderText="Opcional"
                  />
                </div>

                {/* Neto + IVA + Total (con Exento checkbox) */}
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
                    <div style={{ flex: 1 }}>
                      <label>Neto *</label>
                      <div className={styles.inputGroup}>
                        <span className={styles.inputPrefix}>{PREFIJO_MONEDA[formMoneda] || "$"}</span>
                        <input
                          type="text"
                          className="form-control"
                          value={formatMontoDisplay(formMontoTotal, normalizedPais)}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, monto_total: parseMontoInput(e.target.value, formMoneda) }))
                          }
                          placeholder="0"
                          style={{ border: "none", flex: 1 }}
                        />
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label>
                        {nombreImpuesto} {Math.round(tasaImpuesto * 100)}%
                      </label>
                      <div className={styles.inputGroup}>
                        <span className={styles.inputPrefix}>{PREFIJO_MONEDA[formMoneda] || "$"}</span>
                        <input
                          type="text"
                          className="form-control"
                          value={(() => {
                            if (formExentoImpuesto || !formMontoTotal) return "";
                            const iva = parseFloat((Number(formMontoTotal) * tasaImpuesto).toFixed(2));
                            return formatMontoDisplay(iva, normalizedPais);
                          })()}
                          disabled
                          style={{ border: "none", flex: 1, background: "#f5f5f5" }}
                        />
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label>Total</label>
                      <div className={styles.inputGroup}>
                        <span className={styles.inputPrefix}>{PREFIJO_MONEDA[formMoneda] || "$"}</span>
                        <input
                          type="text"
                          className="form-control"
                          value={(() => {
                            if (!formMontoTotal) return "";
                            const n = Number(formMontoTotal);
                            const t = formExentoImpuesto ? n : parseFloat((n * (1 + tasaImpuesto)).toFixed(2));
                            return formatMontoDisplay(t, normalizedPais);
                          })()}
                          disabled
                          style={{ border: "none", flex: 1, background: "#f5f5f5", fontWeight: 600 }}
                        />
                      </div>
                    </div>
                    <select
                      className="form-select"
                      value={formMoneda}
                      onChange={(e) => setForm((p) => ({ ...p, moneda: e.target.value }))}
                      style={{ width: "90px", flex: "none", alignSelf: "flex-end" }}
                    >
                      {monedas.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      cursor: "pointer",
                      margin: "6px 0 0",
                      fontSize: "0.8rem",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formExentoImpuesto}
                      onChange={(e) => setForm((p) => ({ ...p, exento_impuesto: e.target.checked }))}
                    />
                    Exento de {NOMBRE_IMPUESTO[normalizedPais] || "IVA"}
                  </label>
                </div>

                {/* Descripción */}
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Descripción</label>
                  <textarea
                    className="form-control"
                    value={formDescripcion}
                    onChange={(e) => setForm((p) => ({ ...p, descripcion: e.target.value }))}
                    placeholder="Opcional"
                    rows={2}
                  />
                </div>

                {/* Archivo adjunto */}
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Archivo (PDF, imagen)</label>
                  <input
                    type="file"
                    className="form-control"
                    accept=".pdf,.png,.jpg,.jpeg,.webp"
                    onChange={(e) => setArchivo(e.target.files?.[0] || null)}
                  />
                  {archivo && (
                    <small style={{ color: "#666", marginTop: 4 }}>
                      {archivo.name} ({(archivo.size / 1024).toFixed(0)} KB)
                    </small>
                  )}
                </div>

                {/* ═══════════════════════════════════════ */}
                {/* CLASIFICACIÓN CONTABLE                 */}
                {/* ═══════════════════════════════════════ */}
                <div className={`${styles.clasificacionSection}`}>
                  <div className={styles.clasificacionLabel}>Clasificación Contable *</div>
                  <div className={styles.tipoSelector}>
                    <button
                      type="button"
                      className={`${styles.tipoOption} ${formTipoContable === "gasto" ? styles.activeGasto : ""}`}
                      onClick={() => setForm((p) => ({ ...p, tipo_contable: "gasto", cuenta_er: p.cuenta_er }))}
                    >
                      Gasto
                    </button>
                    <button
                      type="button"
                      className={`${styles.tipoOption} ${formTipoContable === "activo" ? styles.activeActivo : ""}`}
                      onClick={() => setForm((p) => ({ ...p, tipo_contable: "activo" }))}
                    >
                      Activo
                    </button>
                  </div>

                  {/* Si GASTO → cuenta ER */}
                  {formTipoContable === "gasto" && (
                    <div className={styles.formGroup} style={{ marginTop: 12 }}>
                      <label>Cuenta del Estado de Resultados *</label>
                      <select
                        className="form-select"
                        value={formCuentaEr}
                        onChange={(e) => setForm((p) => ({ ...p, cuenta_er: e.target.value }))}
                      >
                        <option value="">Seleccionar cuenta...</option>
                        <optgroup label="Costos Fijos">
                          {cuentasFijos.map((c) => (
                            <option key={c.value} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="Costos Variables">
                          {cuentasVariables.map((c) => (
                            <option key={c.value} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </optgroup>
                      </select>
                    </div>
                  )}

                  {/* Si ACTIVO → campos de activo */}
                  {formTipoContable === "activo" && (
                    <div className={styles.formGrid} style={{ marginTop: 12 }}>
                      <div className={styles.formGroup}>
                        <label>Tipo de Activo *</label>
                        <select
                          className="form-select"
                          value={formActivoTipo}
                          onChange={(e) => setForm((p) => ({ ...p, activo_tipo: e.target.value }))}
                        >
                          <option value="">Seleccionar...</option>
                          {TIPOS_ACTIVO.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className={styles.formGroup}>
                        <label>Cantidad</label>
                        <input
                          type="number"
                          className="form-control"
                          value={formActivoCantidad}
                          onChange={(e) => setForm((p) => ({ ...p, activo_cantidad: parseInt(e.target.value) || 1 }))}
                          min={1}
                        />
                      </div>

                      <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                        <label>Descripción del activo *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formActivoDesc}
                          onChange={(e) => setForm((p) => ({ ...p, activo_descripcion: e.target.value }))}
                          placeholder="Ej: MacBook Pro 14 M3"
                        />
                      </div>

                      <div className={styles.formGroup}>
                        <label>N° Serie (opcional)</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formActivoSerie}
                          onChange={(e) => setForm((p) => ({ ...p, activo_numero_serie: e.target.value }))}
                        />
                      </div>

                      <div className={styles.formGroup}>
                        <label>Vida útil</label>
                        <div className={styles.vidaUtilDisplay}>
                          {vidaUtilActivo ? `${vidaUtilActivo} meses` : "Seleccionar tipo de activo"}
                        </div>
                      </div>

                      <div className={styles.formGroup}>
                        <label>Destino</label>
                        <select
                          className="form-select"
                          value={destTipo}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, activo_destino_tipo: e.target.value, activo_destino_id: "" }))
                          }
                        >
                          {DESTINO_TIPOS.map((d) => (
                            <option key={d.value} value={d.value}>
                              {d.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {destTipo && (
                        <div className={styles.formGroup}>
                          <label>{destTipo === "edificio" ? "Edificio" : "Proyecto"}</label>
                          <select
                            className="form-select"
                            value={formActivoDestinoId}
                            onChange={(e) => setForm((p) => ({ ...p, activo_destino_id: e.target.value }))}
                          >
                            <option value="">Seleccionar...</option>
                            {destinoOpciones.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FacturasProveedorTab;
